import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock, Lock, Send, Search, UserCheck, X, RefreshCw, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLUBS = [
  { key: "Diplomatas", emoji: "🤝", color: "#22C55E" },
  { key: "Analistas", emoji: "🧠", color: "#A855F7" },
  { key: "Sentinelas", emoji: "🛡️", color: "#3B82F6" },
  { key: "Exploradores", emoji: "🚀", color: "#EAB308" },
];

const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return err?.message || "Erro inesperado";
};

export default function PublicApply() {
  const [info, setInfo] = useState(null);
  const [done, setDone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", player_ref: null, club: "", motivation: "", discovery_goal: "",
    commitment_level: "full", commitment_notes: "", accepts_substitute: false, leadership_interest: "none", pledge: false,
  });
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [matched, setMatched] = useState(null);   // consultor confirmado da plataforma
  const [manual, setManual] = useState(false);    // "não estou na lista" → escreve o email
  const debounce = useRef(null);

  const onNameChange = (value) => {
    setForm((f) => ({ ...f, name: value, player_ref: null }));
    setMatched(null);
    clearTimeout(debounce.current);
    if (manual || value.trim().length < 3) { setMatches([]); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/applications/public/lookup`, { params: { q: value.trim() } });
        setMatches(r.data?.results || []);
      } catch { setMatches([]); }
      setSearching(false);
    }, 280);
  };

  const pickMatch = (m) => {
    setMatched(m);
    setMatches([]);
    setManual(false);
    setForm((f) => ({ ...f, name: m.name, player_ref: m.ref, email: "", club: m.club || f.club }));
  };

  const clearMatch = () => {
    setMatched(null);
    setForm((f) => ({ ...f, player_ref: null }));
  };

  const applied = matched?.applied || null;                      // candidatura já existente
  const lockedByDecision = applied && applied.status !== "pending";

  useEffect(() => {
    axios.get(`${API}/applications/public/info`)
      .then((r) => setInfo(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.club) return toast.error("Escolhe o teu Clube.");
    if (!form.pledge) return toast.error("Confirma o compromisso para submeter.");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/applications/public`, form);
      setDone(r.data?.updated ? "updated" : "created");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };

  if (!info) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#737373] text-xs uppercase tracking-[0.2em]">A carregar…</div>;
  }

  const domains = (info.allowed_domains || []).map((d) => `@${d}`).join(" ou ");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="public-apply-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#1A1A1A]">
        <div className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #E50000 0%, transparent 45%), radial-gradient(circle at 85% 70%, #3B82F6 0%, transparent 40%)" }} />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-12">
          <div className="inline-flex items-center gap-2 border border-[#E50000] px-3 py-1 mb-6">
            <span className="w-2 h-2 bg-[#E50000] rounded-full animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold">Growth Clubs KW Lead</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
            Do Autoconhecimento<br />
            <span className="text-[#E50000]">ao Crescimento</span>
          </h1>
          <p className="text-lg mt-6 font-bold">
            Este não é apenas um formulário de inscrição.
          </p>
          <p className="text-lg text-[#E50000] font-bold">É o primeiro passo de um compromisso.</p>
          <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed mt-6 max-w-2xl">
            O programa <b className="text-white">Do Autoconhecimento ao Crescimento</b> é uma experiência de
            autoeducação que combina autoconhecimento, vínculos, experimentação, ação e reflexão.
          </p>
          <div className="flex flex-wrap gap-6 mt-8">
            <div>
              <div className="font-display text-4xl font-black text-[#E50000]">{info.quota_active}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">Concorrentes / clube</div>
            </div>
            <div>
              <div className="font-display text-4xl font-black text-[#3B82F6]">4</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">Clubes</div>
            </div>
            <div>
              <div className="font-display text-4xl font-black">3<span className="text-lg">min</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">A preencher</div>
            </div>
          </div>
          {info.deadline_iso && (
            <p className="text-xs text-[#EAB308] mt-6 inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Candidaturas até {new Date(info.deadline_iso).toLocaleDateString("pt-PT")}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {done && (
          <div className="border border-[#22C55E] bg-[#22C55E]/5 p-8 text-center" data-testid="public-apply-done">
            <CheckCircle2 className="w-12 h-12 text-[#22C55E] mx-auto mb-4" />
            <h2 className="font-display text-3xl font-black uppercase tracking-tight">
              {done === "updated" ? "Candidatura atualizada" : "Candidatura enviada"}
            </h2>
            <p className="text-sm text-[#A3A3A3] mt-3 max-w-md mx-auto leading-relaxed">
              Obrigado, {form.name.split(" ")[0]}. Recebemos a tua candidatura ao clube <b className="text-white">{form.club}</b>.
              Enviámos-te um email de confirmação — vamos avisar-te quando houver decisão.
            </p>
          </div>
        )}

        {!done && !info.is_open && (
          <div className="border border-[#333] p-8 text-center" data-testid="public-apply-closed">
            <Lock className="w-10 h-10 text-[#737373] mx-auto mb-4" />
            <h2 className="font-display text-2xl font-black uppercase">Candidaturas fechadas</h2>
            <p className="text-sm text-[#A3A3A3] mt-2">
              O período de candidaturas não está aberto neste momento. Fala com a organização.
            </p>
          </div>
        )}

        {!done && info.is_open && (
          <form onSubmit={submit} className="space-y-8" data-testid="public-apply-form">
            <p className="text-sm text-[#A3A3A3]">
              Se queres fazer parte desta experiência, responde às perguntas seguintes.
            </p>

            <Field n="1" label="Nome completo" hint="Começa a escrever — se já estás na plataforma, aparece na lista">
              <div className="relative">
                <input className="kw-input pr-10" value={form.name} required
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="O teu nome completo" autoComplete="off"
                  data-testid="public-name" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555]">
                  {matched ? <UserCheck className="w-4 h-4 text-[#22C55E]" /> : <Search className="w-4 h-4" />}
                </span>

                {!matched && matches.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-[#121212] border border-[#333] max-h-64 overflow-y-auto"
                    data-testid="public-name-suggestions">
                    {matches.map((m) => (
                      <button key={m.ref} type="button" onClick={() => pickMatch(m)}
                        className="w-full text-left px-4 py-3 hover:bg-[#1E1E1E] border-b border-[#1A1A1A] last:border-0 transition-colors"
                        data-testid={`public-name-option-${m.ref}`}>
                        <div className="text-sm font-bold">{m.name}</div>
                        <div className="text-[11px] text-[#737373] font-mono">
                          {m.email_masked}{m.club ? ` · ${m.club}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {searching && !matched && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#555] mt-2">A procurar…</div>
              )}

              {!matched && !manual && form.name.trim().length >= 3 && !searching && matches.length === 0 && (
                <div className="text-[11px] text-[#737373] mt-2">
                  Não te encontrámos na plataforma — escreve o teu email KW abaixo.
                </div>
              )}

              {!matched && !manual && matches.length > 0 && (
                <button type="button" onClick={() => { setManual(true); setMatches([]); }}
                  className="text-[10px] uppercase tracking-[0.2em] text-[#737373] hover:text-white mt-2 underline"
                  data-testid="public-manual-email">
                  Não sou nenhum destes
                </button>
              )}
            </Field>

            {matched ? (
              <Field n="2" label="Email KW" hint="Já temos o teu email na plataforma — não precisas de o escrever">
                <div className="flex items-center justify-between gap-3 border border-[#22C55E] bg-[#22C55E]/5 px-4 py-3"
                  data-testid="public-email-locked">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#22C55E] mb-1">Confirmado</div>
                    <div className="font-mono text-sm">{matched.email_masked}</div>
                  </div>
                  <button type="button" onClick={clearMatch}
                    className="text-[10px] uppercase tracking-[0.2em] text-[#737373] hover:text-white inline-flex items-center gap-1"
                    data-testid="public-email-change">
                    <X className="w-3 h-3" /> Não sou eu
                  </button>
                </div>
              </Field>
            ) : (
              <Field n="2" label="Email KW" hint={`Tem de ser ${domains}`}>
                <input className="kw-input" type="email" value={form.email} required
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={`nome${(info.allowed_domains || [])[0] ? "@" + info.allowed_domains[0] : ""}`}
                  autoComplete="email" data-testid="public-email" />
              </Field>
            )}

            {applied && (
              <div className={`border p-5 ${lockedByDecision ? "border-[#EAB308] bg-[#EAB308]/5" : "border-[#3B82F6] bg-[#3B82F6]/5"}`}
                data-testid="public-already-applied">
                <div className="flex items-start gap-3">
                  {lockedByDecision
                    ? <AlertTriangle className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
                    : <RefreshCw className="w-5 h-5 text-[#3B82F6] shrink-0 mt-0.5" />}
                  <div>
                    <div className="font-display text-base font-black uppercase tracking-tight">
                      {lockedByDecision ? "A tua candidatura já foi avaliada" : "Já tens candidatura entregue"}
                    </div>
                    <p className="text-sm text-[#A3A3A3] mt-1.5 leading-relaxed">
                      {matched.name.split(" ")[0]}, entregaste a tua candidatura
                      {applied.club ? <> ao clube <b className="text-white">{applied.club}</b></> : null}
                      {applied.created_at ? ` em ${new Date(applied.created_at).toLocaleDateString("pt-PT")}` : ""}.
                      {lockedByDecision
                        ? " Já foi decidida, por isso não pode ser alterada aqui — fala com a organização."
                        : " Se preencheres e submeteres, as tuas respostas são atualizadas e não é criada uma segunda candidatura."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Field n="3" label="Qual é o teu Clube?">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {CLUBS.map((c) => (
                  <button key={c.key} type="button"
                    onClick={() => setForm({ ...form, club: c.key })}
                    className={`p-4 border text-xs font-bold uppercase tracking-wider transition-all duration-200 ${form.club === c.key ? "bg-[#E50000] border-[#E50000] text-white scale-[1.02]" : "border-[#333] text-[#A3A3A3] hover:border-[#666]"}`}
                    style={form.club === c.key ? {} : { borderColor: c.color + "66" }}
                    data-testid={`public-club-${c.key}`}>
                    <div className="text-3xl mb-2">{c.emoji}</div>
                    {c.key}
                  </button>
                ))}
              </div>
            </Field>

            <Field n="4" label="Porque gostarias de participar nesta experiência?" hint="2–3 linhas">
              <textarea className="kw-input min-h-[100px] resize-none" value={form.motivation} required maxLength={1000}
                onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                data-testid="public-motivation" />
            </Field>

            <Field n="5" label="O que gostarias de descobrir, desenvolver ou transformar em ti ao longo deste percurso?">
              <textarea className="kw-input min-h-[100px] resize-none" value={form.discovery_goal} required maxLength={1000}
                onChange={(e) => setForm({ ...form, discovery_goal: e.target.value })}
                data-testid="public-discovery" />
            </Field>

            <Field n="6" label="Estás disponível para te comprometer com as 8 sessões e com os desafios/experiências entre sessões?">
              <div className="space-y-2">
                {[
                  { v: "full", t: "Sim, assumo esse compromisso." },
                  { v: "with_limitations", t: "Tenho algumas limitações que gostaria de explicar." },
                ].map((o) => (
                  <label key={o.v}
                    className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${form.commitment_level === o.v ? "border-[#E50000] bg-[#E50000]/5" : "border-[#333] hover:border-[#555]"}`}>
                    <input type="radio" name="commitment" checked={form.commitment_level === o.v}
                      onChange={() => setForm({ ...form, commitment_level: o.v })}
                      className="accent-[#E50000]" data-testid={`public-commit-${o.v}`} />
                    <span className="text-sm">{o.t}</span>
                  </label>
                ))}
              </div>
              {form.commitment_level === "with_limitations" && (
                <textarea className="kw-input mt-2 min-h-[80px]" placeholder="Descreve as tuas limitações…"
                  value={form.commitment_notes}
                  onChange={(e) => setForm({ ...form, commitment_notes: e.target.value })}
                  data-testid="public-commit-notes" />
              )}
            </Field>

            <Field n="7" label="Se não fores selecionado para coopetir, gostarias de assumir um cargo no teu clube?">
              <p className="text-xs text-[#737373] mb-3 leading-relaxed">
                O <strong className="text-white">Secretário do Clube</strong> acompanha e apoia o plantel
                de um clube <strong className="text-white">diferente do teu perfil</strong>, para trazer um
                olhar de fora. O <strong className="text-white">Presidente do Clube</strong> é do perfil
                do próprio clube e zela pela união e pela liderança do clube. Nenhum deles coopete — a escolha
                final é do plantel.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: "none", t: "Não, obrigado" },
                  { v: "coach", t: "Secretário do Clube" },
                  { v: "administrator", t: "Presidente do Clube" },
                  { v: "both", t: "Qualquer um dos dois" },
                ].map((o) => (                  <button key={o.v} type="button"
                    onClick={() => setForm({ ...form, leadership_interest: o.v })}
                    className={`px-6 py-3 text-xs uppercase tracking-widest font-bold border transition-colors ${form.leadership_interest === o.v ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3] hover:border-[#666]"}`}
                    data-testid={`public-leadership-${o.v}`}>
                    {o.t}
                  </button>
                ))}
              </div>
            </Field>

            <label className="flex items-start gap-3 p-5 border border-[#E50000]/40 bg-[#E50000]/5 cursor-pointer">
              <input type="checkbox" checked={form.pledge}
                onChange={(e) => setForm({ ...form, pledge: e.target.checked })}
                className="accent-[#E50000] w-4 h-4 mt-0.5" data-testid="public-pledge" />
              <span className="text-sm leading-relaxed">
                Compreendo que existem vagas limitadas e que a candidatura representa a minha intenção de
                <b> participar ativamente, experimentar, executar, refletir e contribuir para o crescimento do meu Clube</b>.
                <br /><span className="text-[#EAB308] font-bold">Sim, quero candidatar-me.</span>
              </span>
            </label>

            <button type="submit" disabled={saving || !form.pledge || lockedByDecision}
              className="kw-btn-primary w-full flex items-center justify-center gap-2 !py-4 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="public-submit">
              {applied && !lockedByDecision ? <RefreshCw className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {saving ? "A submeter…"
                : lockedByDecision ? "Candidatura já avaliada"
                : applied ? "Atualizar candidatura" : "Submeter candidatura"}
            </button>
          </form>
        )}

        <p className="text-[10px] uppercase tracking-[0.2em] text-[#444] text-center mt-12 border-t border-[#1A1A1A] pt-6">
          Coopetimos para crescer. Não para ganhar a qualquer custo.
        </p>
      </div>
    </div>
  );
}

function Field({ n, label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-display text-xl font-black text-[#E50000]">{n}</span>
        <label className="text-sm md:text-base font-bold leading-snug">{label}</label>
      </div>
      {hint && <div className="text-[10px] uppercase tracking-[0.15em] text-[#737373] mb-2">{hint}</div>}
      {children}
    </div>
  );
}
